import { useEffect, useRef, useState } from 'react';

import { Accordion } from '@deliveryhero/pd-cookbook/components/Accordion';
import { Grid } from '@deliveryhero/pd-cookbook/components/Grid';
import { GridItem } from '@deliveryhero/pd-cookbook/components/GridItem';
import IcClockFilled from '@deliveryhero/pd-cookbook/components/Icons/IcClockFilled';
import IcPlus from '@deliveryhero/pd-cookbook/components/Icons/IcPlus';
import IcSuccessFilled from '@deliveryhero/pd-cookbook/components/Icons/IcSuccessFilled';
import IcWallet from '@deliveryhero/pd-cookbook/components/Icons/IcWallet';
import { Tag, TagVariant } from '@deliveryhero/pd-cookbook/components/Tag';
import { Typography } from '@deliveryhero/pd-cookbook/components/Typography';
import classNames from 'classnames';
import { Card } from 'src/components/Card';
import { QueryStatus } from 'types';

import { LoadingAnimation } from 'components/LoadingAnimation';

import { LabelText } from '../constants';

import { GiftCardPaymentFlowProps } from './types';

import './styles.scss';

export const GiftCardPaymentFlow = (props: GiftCardPaymentFlowProps) => {
  const {
    details,
    id,
    external_order_number,
    handleUpload,
    uploadState,
    isModal
  } = props;

  const [isExpanded, setIsExpanded] = useState(0);
  const [fileError, setFileError] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleChange = (index: number) => {
    setIsExpanded(index === isExpanded ? 0 : index);
  };

  const renderTag = () => {
    if (fileError) {
      return (
        <Tag
          id="error"
          label={LabelText.GIFT_CARD_PAYMENT_FLOW.UPLOAD_STATUS_FILE_ERROR}
          variant={TagVariant.ERROR}
        ></Tag>
      );
    }
    if (uploadState === QueryStatus.loading) {
      return (
        <Tag
          id="uploading"
          label={LabelText.GIFT_CARD_PAYMENT_FLOW.UPLOAD_STATUS_LOADING}
          variant={TagVariant.SECONDARY}
        ></Tag>
      );
    } else if (uploadState === QueryStatus.success) {
      return (
        <Tag
          id="success"
          label={LabelText.GIFT_CARD_PAYMENT_FLOW.UPLOAD_STATUS_SUCCESS}
          variant={TagVariant.SUCCESS}
        ></Tag>
      );
    } else if (uploadState === QueryStatus.error) {
      return (
        <Tag
          id="error"
          label={LabelText.GIFT_CARD_PAYMENT_FLOW.UPLOAD_STATUS_ERROR}
          variant={TagVariant.ERROR}
        ></Tag>
      );
    } else {
      return (
        <Tag
          id="pending"
          label={LabelText.GIFT_CARD_PAYMENT_FLOW.UPLOAD_STATUS_IDLE}
          variant={TagVariant.ELEVATED}
        ></Tag>
      );
    }
  };

  const handleFileUpload = (file: File | null) => {
    if (!file) return;
    const fileSize = file.size;
    const maxSize = 1024 * 1024 * 10; // 10MB

    if (fileSize >= maxSize) {
      setFileError(true);
    } else {
      handleUpload(id, file);
    }
  };

  const renderUpload = () => {
    return (
      <Grid gridTemplateColumns="auto 1fr" columnGap="16px">
        <div
          className={classNames('gift-card-upload-box', {
            'gift-card-upload-box--success': uploadState === QueryStatus.success
          })}
          onClick={() => fileInputRef.current?.click()}
        >
          {uploadState === QueryStatus.success && !fileError ? (
            <IcSuccessFilled fill="success" />
          ) : uploadState === QueryStatus.loading ? (
            <LoadingAnimation />
          ) : (
            <IcPlus fill="interaction-primary" />
          )}
          <input
            accept="image/png, image/gif, image/jpeg, application/pdf"
            type="file"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              handleFileUpload(
                event.target.files ? event.target.files[0] : null
              )
            }
            data-testid="receiptUpload"
            ref={fileInputRef}
            style={{ display: 'none' }}
          />
        </div>
        <GridItem>
          <Typography
            type="paragraph-sm"
            color="neutral-primary"
            marginBottom="xs"
          >
            {LabelText.GIFT_CARD_PAYMENT_FLOW.UPLOAD_DESCRIPTION}
          </Typography>
          {renderTag()}
        </GridItem>
      </Grid>
    );
  };

  const renderBankDetails = () => {
    return (
      <>
        {details &&
          details.map((detail, index) => {
            return (
              <div
                key={`qc-bank-details-row-${index}`}
                className="gift-card-payment-qc-bank-details"
              >
                <Typography
                  type="label-lg"
                  color="neutral-primary"
                  marginRight="xxs"
                  className="no-wrap"
                >
                  {`${detail[0]}:`}
                </Typography>
                <Typography
                  type="paragraph-md"
                  color="neutral-primary"
                  className="no-wrap"
                >
                  {detail[1]}
                </Typography>
              </div>
            );
          })}
        <div style={{ display: 'flex' }}>
          <Typography type="label-lg" color="neutral-primary" marginRight="xxs">
            Reference:
          </Typography>
          <Typography type="paragraph-md" color="neutral-primary">
            {external_order_number}
          </Typography>
        </div>
      </>
    );
  };

  const renderPaymentVerificationString = () => {
    return (
      <div className="gift-card-payment-flow-header">
        <IcClockFilled fill="information" />
        <Typography type="paragraph-sm" color="neutral-primary" marginLeft="xs">
          {LabelText.GIFT_CARD_PAYMENT_FLOW.STEP_THREE_DETAILS}
        </Typography>
      </div>
    );
  };

  const renderAccordion = (
    index: number,
    title: string,
    content: JSX.Element
  ) => {
    return (
      <Accordion
        id={`accordion-step-${index}`}
        label={title}
        variant="border-box"
        onChange={() => handleChange(index)}
        isExpanded={isExpanded == index}
      >
        {content}
      </Accordion>
    );
  };

  useEffect(() => {
    // reset error state on each new file upload
    if (uploadState === QueryStatus.loading) setFileError(false);
  }, [uploadState]);

  return (
    <Card style={{ ...(isModal && { padding: '24px 0px 0px 0px' }) }}>
      <Grid rowGap="16px">
        {!isModal && (
          <>
            <div className="gift-card-payment-flow-header">
              <IcWallet fill="interaction-primary" />
              <Typography
                type="title-md"
                color="neutral-primary"
                marginLeft="xs"
              >
                {LabelText.GIFT_CARD_PAYMENT_FLOW.TITLE}
              </Typography>
            </div>
            <Typography type="paragraph-md" color="neutral-primary">
              {LabelText.GIFT_CARD_PAYMENT_FLOW.DESCRIPTION}
            </Typography>
          </>
        )}
        {renderAccordion(
          1,
          LabelText.GIFT_CARD_PAYMENT_FLOW.STEP_ONE_TITLE,
          renderBankDetails()
        )}
        {renderAccordion(
          2,
          LabelText.GIFT_CARD_PAYMENT_FLOW.STEP_TWO_TITLE,
          renderUpload()
        )}
        {!isModal &&
          renderAccordion(
            3,
            LabelText.GIFT_CARD_PAYMENT_FLOW.STEP_THREE_TITLE,
            renderPaymentVerificationString()
          )}
      </Grid>
    </Card>
  );
};
